import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSun, faMoon, faExpand, faCompress } from '@fortawesome/free-solid-svg-icons';
import { logOut } from '../../contexts/loginContexts';
import config from '../../config';
import { getStoredAuthUser } from '../../utils/authUser';

const BRAND_GRADIENT = 'linear-gradient(135deg, #C04C2A 0%, #8B2A15 55%, #4A1B13 100%)';

const buildInitials = (value) => {
  const parts = String(value || '')
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2);
  if (!parts.length) return 'U';
  return parts.map((item) => item[0]?.toUpperCase() || '').join('') || 'U';
};

const getImageCandidates = (path) => {
  if (!path) return [];
  const normalizedPath = String(path).trim().replace(/\\/g, '/');
  if (!normalizedPath) return [];
  if (
    normalizedPath.startsWith('blob:') ||
    normalizedPath.startsWith('data:') ||
    normalizedPath.startsWith('http://') ||
    normalizedPath.startsWith('https://')
  ) return [normalizedPath];

  let cleanPath = normalizedPath.startsWith('~') ? normalizedPath.substring(1) : normalizedPath;
  if (!cleanPath.startsWith('/')) cleanPath = `/${cleanPath}`;
  cleanPath = cleanPath.replace(/\/{2,}/g, '/');

  let apiBase = '';
  let serverOrigin = '';
  try {
    const url = new URL(config.baseurl, window.location.origin);
    apiBase = url.href.replace(/\/+$/, '');
    serverOrigin = url.origin;
  } catch {
    apiBase = String(config.baseurl || window.location.origin).replace(/\/+$/, '');
    serverOrigin = window.location.origin;
  }

  return [...new Set([`${apiBase}${cleanPath}`, `${serverOrigin}${cleanPath}`].filter(Boolean))];
};

const Navbar = ({ isDropdownOpen, setIsDropdownOpen }) => {
  const navigate = useNavigate();
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [user, setUser] = useState(() => getStoredAuthUser());
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const savedTheme = localStorage.getItem('theme');
    return savedTheme ? savedTheme === 'dark' : false;
  });
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [photoIndex, setPhotoIndex] = useState(0);

  const userDisplayName = useMemo(() => user?.fullName || user?.userName || user?.email || 'User', [user]);
  const userDisplayEmail = useMemo(() => user?.workEmail || user?.email || 'No email', [user]);
  const photoCandidates = useMemo(() => getImageCandidates(user?.photoUrlWithPath), [user?.photoUrlWithPath]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('#dropdown-user') && !event.target.closest('.user-menu')) {
        setIsUserMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const syncUser = () => setUser(getStoredAuthUser());
    syncUser();
    window.addEventListener('storage', syncUser);
    return () => window.removeEventListener('storage', syncUser);
  }, []);

  useEffect(() => { setPhotoIndex(0); }, [user?.photoUrlWithPath]);

  // ── Theme toggle: set dark class on both html and body ──
  useEffect(() => {
    document.documentElement.classList.toggle('dark', isDarkMode);
    document.body.classList.toggle('dark', isDarkMode);
    localStorage.setItem('theme', isDarkMode ? 'dark' : 'light');
  }, [isDarkMode]);

  const toggleDarkMode = () => setIsDarkMode((prev) => !prev);

  const toggleFullscreen = () => {
    if (!isFullscreen) {
      document.documentElement.requestFullscreen?.();
    } else {
      document.exitFullscreen?.();
    }
    setIsFullscreen((prev) => !prev);
  };

  const handleProfileImageError = () => {
    setPhotoIndex((current) => (current + 1 < photoCandidates.length ? current + 1 : current));
  };

  const logout = async () => {
    const refreshToken = localStorage.getItem('refreshToken');
    const payload = { UserId: user?.userId, RefreshToken: refreshToken };
    try {
      const response = await logOut(`${config.baseurl}Auth/logOut`, payload);
      if (response?.status === 200 || response?.message === 'Logged out successfully') {
        localStorage.removeItem('authtoken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('user');
        localStorage.removeItem('tokenExpiry');
        navigate('/login', { replace: true });
      }
    } catch (err) {
      console.error(err);
    }
  };

  const shouldShowImage = photoCandidates.length > 0 && photoIndex < photoCandidates.length;

  return (
    <nav className="fixed top-0 z-50 w-full bg-white dark:bg-[#161B22] border-b border-gray-100 dark:border-gray-800 transition-colors duration-300">
      <div className="px-3 py-2.5 lg:px-5 lg:pl-3">
        <div className="flex items-center justify-between">

          {/* ── Left: hamburger + logo ── */}
          <div className="flex items-center gap-2">
            <button
              onClick={() => setIsDropdownOpen((prev) => !prev)}
              type="button"
              className="inline-flex items-center p-2 rounded-xl text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path clipRule="evenodd" fillRule="evenodd"
                  d="M2 4.75A.75.75 0 012.75 4h14.5a.75.75 0 010 1.5H2.75A.75.75 0 012 4.75zm0 10.5a.75.75 0 01.75-.75h7.5a.75.75 0 010 1.5h-7.5a.75.75 0 01-.75-.75zM2 10a.75.75 0 01.75-.75h14.5a.75.75 0 010 1.5H2.75A.75.75 0 012 10z" />
              </svg>
            </button>

            <Link to="/dashboard" className="flex items-center gap-2">
              {/* Show different logo for light/dark */}
              <img
                src={isDarkMode ? '/dark.png' : '/light.png'}
                className="h-8"
                alt="BanglaFire Logo"
                onError={(e) => { e.target.src = '/light.png'; }}
              />
            </Link>
          </div>

          {/* ── Right: theme toggle, fullscreen, user ── */}
          <div className="flex items-center gap-1">

            {/* Dark / Light toggle */}
            <button
              onClick={toggleDarkMode}
              title={isDarkMode ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
              className="relative inline-flex h-9 w-9 items-center justify-center rounded-xl transition-all hover:scale-105 active:scale-95"
              style={{ background: isDarkMode ? 'linear-gradient(135deg,#f59e0b,#d97706)' : BRAND_GRADIENT }}
            >
              <FontAwesomeIcon
                icon={isDarkMode ? faSun : faMoon}
                className="text-white"
                style={{ fontSize: 14 }}
              />
            </button>

            {/* Fullscreen toggle */}
            <button
              onClick={toggleFullscreen}
              title={isFullscreen ? 'Exit Fullscreen' : 'Enter Fullscreen'}
              className="inline-flex h-9 w-9 items-center justify-center rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-500 dark:text-gray-400 transition-all hover:border-gray-300 dark:hover:bg-gray-700 hover:scale-105 active:scale-95"
            >
              <FontAwesomeIcon icon={isFullscreen ? faCompress : faExpand} style={{ fontSize: 14 }} />
            </button>

            {/* User menu */}
            <div className="relative flex items-center ms-1 user-menu">
              <button
                type="button"
                onClick={() => setIsUserMenuOpen((prev) => !prev)}
                className="flex items-center gap-2.5 rounded-xl px-2 py-1.5 transition hover:bg-gray-100 dark:hover:bg-gray-800"
              >
                {shouldShowImage ? (
                  <img
                    className="w-8 h-8 rounded-full object-cover ring-2 ring-offset-1 ring-offset-white dark:ring-offset-[#161B22]"
                    style={{ ringColor: '#C04C2A' }}
                    src={photoCandidates[photoIndex]}
                    alt={userDisplayName}
                    onError={handleProfileImageError}
                  />
                ) : (
                  <div
                    className="flex h-8 w-8 items-center justify-center rounded-full text-xs font-bold text-white shadow-sm"
                    style={{ background: BRAND_GRADIENT }}
                  >
                    {buildInitials(userDisplayName)}
                  </div>
                )}
                <div className="hidden text-left sm:block">
                  <div className="max-w-[140px] truncate text-sm font-semibold text-gray-800 dark:text-white leading-tight">
                    {userDisplayName}
                  </div>
                  <div className="max-w-[160px] truncate text-xs text-gray-500 dark:text-gray-400 leading-tight">
                    {userDisplayEmail}
                  </div>
                </div>
                <svg className="w-3.5 h-3.5 text-gray-400 dark:text-gray-500 hidden sm:block" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {isUserMenuOpen && (
                <div
                  className="absolute right-0 z-50 mt-[230px] w-56 rounded-2xl border border-gray-100 dark:border-gray-700 bg-white dark:bg-[#1e2533] shadow-xl overflow-hidden"
                  id="dropdown-user"
                >
                  {/* User info header */}
                  <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-700">
                    <div className="flex items-center gap-3">
                      {shouldShowImage ? (
                        <img className="w-9 h-9 rounded-full object-cover" src={photoCandidates[photoIndex]} alt={userDisplayName} onError={handleProfileImageError} />
                      ) : (
                        <div className="flex h-9 w-9 items-center justify-center rounded-full text-xs font-bold text-white" style={{ background: BRAND_GRADIENT }}>
                          {buildInitials(userDisplayName)}
                        </div>
                      )}
                      <div className="min-w-0">
                        <p className="truncate text-sm font-semibold text-gray-900 dark:text-white">{userDisplayName}</p>
                        <p className="truncate text-xs text-gray-500 dark:text-gray-400">{userDisplayEmail}</p>
                      </div>
                    </div>
                  </div>

                  <ul className="py-1.5 px-1.5">
                    <li>
                      <Link
                        to="/dashboard"
                        onClick={() => setIsUserMenuOpen(false)}
                        className="flex items-center gap-2.5 rounded-xl px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                      >
                        Dashboard
                      </Link>
                    </li>
                    <li>
                      <Link
                        to="/profile"
                        onClick={() => setIsUserMenuOpen(false)}
                        className="flex items-center gap-2.5 rounded-xl px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                      >
                        Profile
                      </Link>
                    </li>
                    <li className="mt-1 border-t border-gray-100 dark:border-gray-700 pt-1">
                      <button
                        type="button"
                        onClick={logout}
                        className="flex w-full items-center gap-2.5 rounded-xl px-3 py-2 text-left text-sm font-semibold text-rose-600 dark:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-900/20 transition-colors"
                      >
                        Sign out
                      </button>
                    </li>
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
